import { ActionRow } from "@/components/rail-kit/ActionRow";
import { Accordion } from "@/components/ui/accordion";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { ROUTES } from "@/constants/routes";
import { useOpenSettings } from "@/context/OpenSettingsContext";
import { RailFavoritesDropTarget } from "@/dnd";
import { ConnectorsRailDraggableConnectorRow } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/connectors/ConnectorsRailDraggableConnectorRow";
import { useSelectableIntegrationConnections } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/connectors/useSelectableIntegrationConnections";
import { RailGroup } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailGroup";
import {
  RailRowMenu,
  railRowMenuProps,
} from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailRowMenu";
import { RailZeroState } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailZeroState";
import { railZeroStateCopy } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/railZeroStateCopy";
import { useRailRowContextMenu } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/useRailRowContextMenu";
import { RESOURCE_TAG_FAVORITE } from "@cohesive/api-client";
import { Blocks, Plus, Star } from "lucide-react";

export function ConnectorsRail() {
  const openSettings = useOpenSettings();
  const addConnectorMenu = useRailRowContextMenu();
  const { connections, isPending, isError } =
    useSelectableIntegrationConnections();

  // A cold first load renders nothing rather than flashing the add-your-first
  // CTA at users who have connectors.
  if (isPending) {
    return <div className="min-h-0 flex-1" />;
  }

  if (isError) {
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center px-4 py-8">
        <p className="text-center text-sm text-n-40">
          Couldn&apos;t load connectors.
        </p>
      </div>
    );
  }

  // Favorites are derived client-side from the already-loaded connections.
  const favoriteRows = connections
    .filter((connection) => connection.tags?.includes(RESOURCE_TAG_FAVORITE))
    .map((connection) => (
      <ConnectorsRailDraggableConnectorRow
        key={connection.id}
        connection={connection}
        dragNamespace="favorites"
      />
    ));

  const connectorRows = connections.map((connection) => (
    <ConnectorsRailDraggableConnectorRow
      key={connection.id}
      connection={connection}
      dragNamespace="all"
    />
  ));

  return (
    // Padding belongs to the scroller inside, never to the flex item itself:
    // a border-box element can't shrink below its own padding, which would
    // floor this rail and stop the drawer short of where the other rails
    // let it reach.
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-2 pb-2 pt-3">
        <Accordion
          type="multiple"
          defaultValue={["connectors-all"]}
          className="flex flex-col gap-2"
        >
          <RailFavoritesDropTarget resourceType="integration-connection">
            <RailGroup
              groupId="connectors-favorites"
              label="Favorites"
              icon={Star}
              noCap
              items={favoriteRows}
              emptyState={
                <RailZeroState
                  icon={Star}
                  variant="passive"
                  title="Pin what matters."
                  description="Add a connector to favorites and it stays here."
                />
              }
            />
          </RailFavoritesDropTarget>
          <RailGroup
            groupId="connectors-all"
            label="Connectors"
            icon={Blocks}
            noCap
            onContextMenu={addConnectorMenu.onContextMenu}
            items={[
              // The list's own closing verb rides INSIDE the section so it
              // collapses with it (Aug 2026 ruling). It is the section's one
              // create control — no head plus — so it stays under the quiet
              // zero-state line when the list is empty.
              ...(connectorRows.length > 0
                ? connectorRows
                : [
                    <RailZeroState
                      key="connectors-empty"
                      icon={Blocks}
                      accent="plum"
                      title={railZeroStateCopy.connectors.all.title}
                      description={railZeroStateCopy.connectors.all.description}
                    />,
                  ]),
              <ActionRow
                key="new-connector"
                icon={Plus}
                label="New connector"
                align="row"
                onClick={() => openSettings(ROUTES.SETTINGS.CONNECTORS)}
              />,
            ]}
          />
        </Accordion>
      </div>

      <RailRowMenu
        {...railRowMenuProps(addConnectorMenu)}
        kebab={false}
        label="Connectors"
      >
        <DropdownMenuItem
          onClick={() => openSettings(ROUTES.SETTINGS.CONNECTORS)}
        >
          <Plus />
          New connector
        </DropdownMenuItem>
      </RailRowMenu>
    </div>
  );
}
